import React, { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Edit,
} from '@syncfusion/ej2-react-grids';
import './DemandView.css';

interface Demand {
  id: string;
  materialName: string;
  demandDate: string;
  dueTime: string;
  quantity: number;
  source: string;
  priority: number;
}

interface DemandEntry {
  id: string;
  dueTime: string;
  quantity: number;
  source: string;
}

interface DayDemand {
  date: string;
  totalQuantity: number;
  demands: DemandEntry[];
}

interface Product {
  productId: number;
  productName: string;
  weekDemands: DayDemand[];
}

interface DemandViewProps {
  startDate: string; // Start date for the grid (always today)
  planningHorizonStart: string; // Start of planning horizon (for highlighting)
  planningHorizonEnd: string; // End of planning horizon (for highlighting and filtering)
  demands: Demand[];
  allMaterials?: Array<{id: string, name: string}>; // Optional: all materials to show even without demands
  /** When true, grid fills remaining height (e.g. in wizard). Default: fixed 400px. */
  fillHeight?: boolean;
}

// Helper function to create week data from demands
const createWeekDataFromDemands = (demands: Demand[], startDate: string, _planningHorizonStart: string, _planningHorizonEnd: string, allMaterials?: Array<{id: string, name: string}>): Product[] => {
  // Validate and handle invalid dates
  if (!startDate || startDate.trim() === '') {
    console.warn('Invalid startDate provided to createWeekDataFromDemands:', startDate);
    return [];
  }
  
  // Parse startDate as local date (YYYY-MM-DD format)
  const [year, month, day] = startDate.split('-').map(Number);
  const start = new Date(year, month - 1, day); // month is 0-indexed
  
  // Check if the date is valid
  if (isNaN(start.getTime())) {
    console.warn('Invalid date created from startDate:', startDate);
    return [];
  }
  
  const weekDates: string[] = [];
  
  // Generate next 7 days starting from startDate
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    // Format as YYYY-MM-DD using local date
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    weekDates.push(dateString);
  }

  // Filter demands to the display week (7 days) - all entries, not only the planning horizon.
  // The planning horizon is used only to highlight the columns (light green).
  const getDatePart = (dateStr: string) => dateStr?.split('T')[0] || dateStr || '';
  const displayWeekEndDate = new Date(start);
  displayWeekEndDate.setDate(start.getDate() + 6);
  displayWeekEndDate.setHours(23, 59, 59, 999);

  const filteredDemands = demands.filter(demand => {
    const datePart = getDatePart(demand.demandDate);
    if (!datePart) return false;
    const [demandYear, demandMonth, demandDay] = datePart.split('-').map(Number);
    const demandDate = new Date(demandYear, demandMonth - 1, demandDay);
    return demandDate >= start && demandDate <= displayWeekEndDate;
  });

  // Group demands by material
  const demandsByMaterial = filteredDemands.reduce((acc, demand) => {
    if (!acc[demand.materialName]) {
      acc[demand.materialName] = [];
    }
    acc[demand.materialName].push(demand);
    return acc;
  }, {} as Record<string, Demand[]>);

  // Get all material names (from filtered demands or from allMaterials parameter)
  // ALWAYS include all materials, even if they have no demands
  const allMaterialNames = new Set<string>();
  if (allMaterials) {
    allMaterials.forEach(m => allMaterialNames.add(m.name));
  }
  filteredDemands.forEach(d => allMaterialNames.add(d.materialName));

  // Convert to Product format - include all materials, even without demands
  const products: Product[] = Array.from(allMaterialNames).map((materialName, index) => {
    const materialDemands = demandsByMaterial[materialName] || [];
    const weekDemands: DayDemand[] = weekDates.map(date => {
      const dayDemands = materialDemands.filter(demand => getDatePart(demand.demandDate) === date);
      return {
        date,
        totalQuantity: dayDemands.reduce((sum, d) => sum + d.quantity, 0),
        demands: dayDemands.map(d => ({
          id: d.id,
          dueTime: d.dueTime,
          quantity: d.quantity,
          source: d.source
        }))
      };
    });

    return {
      productId: index + 1,
      productName: materialName,
      weekDemands
    };
  });

  return products;
};

// Helper functions for date formatting
const formatDate = (dateString: string, locale: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-US', { 
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (timeString: string) => {
  return timeString.substring(0, 5); // Show only HH:mm
};

// getWeekdayName function removed - not used

export const DemandView: React.FC<DemandViewProps> = React.memo(({ startDate, planningHorizonStart, planningHorizonEnd, demands, allMaterials, fillHeight }) => {
  const { t, i18n } = useTranslation(['common']);
  const gridRef = useRef<any>(null);

  const productData = useMemo(
    () => createWeekDataFromDemands(demands, startDate, planningHorizonStart, planningHorizonEnd, allMaterials),
    [demands, startDate, planningHorizonStart, planningHorizonEnd, allMaterials]
  );

  const isInPlanningHorizon = (dateString: string) => {
    if (!planningHorizonStart || !planningHorizonEnd) return false;
    
    // Parse dates as local dates to avoid timezone issues
    const [dateYear, dateMonth, dateDay] = dateString.split('-').map(Number);
    const [startYear, startMonth, startDay] = planningHorizonStart.split('-').map(Number);
    const [endYear, endMonth, endDay] = planningHorizonEnd.split('-').map(Number);
    
    const date = new Date(dateYear, dateMonth - 1, dateDay);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    
    return date >= start && date <= end;
  };

  const createTooltipContent = (data: any, dateString: string) => {
    if (!data || !data.weekDemands) {
      return t('demandView.tooltip.noDemands', { date: formatDate(dateString, i18n.language) });
    }
    
    const day = data.weekDemands.find((d: DayDemand) => d.date === dateString);
    if (!day || day.demands.length === 0) {
      return t('demandView.tooltip.noDemands', { date: formatDate(dateString, i18n.language) });
    }

    const demandDetails = day.demands.map((demand: DemandEntry) => {
      const sourceText =
        demand.source === 'ORDER'
          ? t('demandView.tooltip.order')
          : demand.source === 'EFFECTIVE'
            ? t('demandView.tooltip.effectiveNet', 'Netto (keine Bestellung/Prognose)')
            : demand.source === 'FORECAST'
              ? t('demandView.tooltip.forecast')
              : t('demandView.tooltip.order');
      const timePart =
        demand.dueTime && String(demand.dueTime).length >= 4 ? formatTime(demand.dueTime) : '—';
      return `• ${timePart} - ${demand.quantity} (${sourceText})`;
    }).join('\n');

    const planningHorizonText = isInPlanningHorizon(dateString) ? ` (${t('demandView.legend.planningHorizon')})` : '';

    const totalText = i18n.language === 'de' ? `Gesamtmenge: ${day.totalQuantity}` : `Total: ${day.totalQuantity} units`;
    return `${data.productName} - ${formatDate(dateString, i18n.language)}${planningHorizonText}\n${totalText}\n\n${t('demandView.tooltip.demandDetails')}\n${demandDetails}`;
  };

  const renderDayColumn = (dayIndex: number, dateString: string) => (
    <ColumnDirective 
      field={`day${dayIndex + 1}`} 
      headerText={formatDate(dateString, i18n.language)} 
      width="120" 
      textAlign="Right"
      headerTemplate={(_props: any) => {
        const isPlanningHorizon = isInPlanningHorizon(dateString);
        
        return (
          <div className={isPlanningHorizon ? 'planning-horizon-header' : 'regular-header'}>
            {formatDate(dateString, i18n.language)}
          </div>
        );
      }}
      valueAccessor={(_field: string, data: any) => {
        if (!data || !data.weekDemands) return null;
        const day = data.weekDemands.find((d: DayDemand) => d.date === dateString);
        if (!day || !day.demands || day.demands.length === 0) return null;
        return day.totalQuantity;
      }}
      template={(props: any) => {
        const data = props.data || props.rowData || props;
        if (!data || !data.weekDemands) {
          return (
            <span 
              className="demand-cell"
              data-planning-horizon={isInPlanningHorizon(dateString)}
              title={createTooltipContent(data, dateString)}
            >
              {'\u00a0'}
            </span>
          );
        }
        const day = data.weekDemands.find((d: DayDemand) => d.date === dateString);
        const hasDemands = day && day.demands && day.demands.length > 0;
        const total = day ? day.totalQuantity : 0;
        const hasForecastDemands = day && day.demands && day.demands.some((d: DemandEntry) => d.source === 'FORECAST');
        const hasOrderDemands = day && day.demands && day.demands.some((d: DemandEntry) => d.source === 'ORDER');
        
        return (
          <span 
            className={`demand-cell ${hasDemands ? 'has-demands' : 'no-demands'} ${hasForecastDemands ? 'has-forecast' : ''} ${hasOrderDemands ? 'has-order' : ''}`}
            data-planning-horizon={isInPlanningHorizon(dateString)}
            title={createTooltipContent(data, dateString)}
          >
            {hasDemands ? total : '\u00a0'}
          </span>
        );
      }}
    />
  );

  const onRowDataBound = (args: any) => {
    if (!planningHorizonStart || !planningHorizonEnd) return;
    
    const cells = args.row.querySelectorAll('.e-rowcell');
    // The first cell is the product name, the next are the days
    for (let i = 1; i < cells.length; i++) {
      const colIndex = i - 1;
      // Parse startDate as local date
      const [year, month, day] = startDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + colIndex);
      
      // Use the same logic as isInPlanningHorizon
      const [startYear, startMonth, startDay] = planningHorizonStart.split('-').map(Number);
      const [endYear, endMonth, endDay] = planningHorizonEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      
      const checkDate = new Date(year, month - 1, day);
      checkDate.setDate(checkDate.getDate() + colIndex);
      
      // Remove existing class first
      cells[i].classList.remove('planning-horizon-column');
      
      if (checkDate >= start && checkDate <= end) {
        cells[i].classList.add('planning-horizon-column');
      }
    }
  };

  return (
    <div className={fillHeight ? 'flex flex-col flex-1 min-h-0 gap-4' : 'space-y-4'}>
      {/* Legend: orders and forecast */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-700 flex-shrink-0">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded demand-legend-orders" title={t('demandView.legend.orders')}></div>
          <span>{t('demandView.legend.orders')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded demand-legend-forecast" title={t('demandView.legend.forecast')}></div>
          <span>{t('demandView.legend.forecast')}</span>
        </div>
      </div>

      {productData.length === 0 && (!allMaterials || allMaterials.length === 0) ? (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600 text-center">
            {t('productionSchedule.wizard.demandView.noDemands', 'No demands found for the selected time period.')}
          </p>
        </div>
      ) : (
        <div
          className="border border-gray-200 rounded-lg overflow-hidden demand-view-container flex-1 min-h-0"
          style={fillHeight ? undefined : { height: '400px' }}
        >
          <GridComponent
            locale="de-DE"
            ref={gridRef}
            key={`grid-${planningHorizonStart}-${planningHorizonEnd}`}
            dataSource={productData}
            height="100%"
            allowPaging={false}
            allowSorting={false}
            allowFiltering={false}
            cssClass="demand-view-grid"
            rowDataBound={onRowDataBound}
          >
            <ColumnsDirective>
              <ColumnDirective 
                field="productName" 
                headerText={t('demandView.grid.product')} 
                width="200"
                headerTemplate={(_props: any) => (
                  <div style={{ marginLeft: '12px', textAlign: 'left' }}>
                    {t('demandView.grid.product')}
                  </div>
                )}
                template={(props: any) => {
                  const data = props.data || props.rowData || props;
                  if (!data || !data.productName) {
                    return <span>Loading...</span>;
                  }
                  return (
                    <span style={{ fontWeight: 'bold', marginLeft: '12px' }}>
                      {data.productName}
                    </span>
                  );
                }}
              />
              {Array.from({ length: 7 }, (_, i) => {
                // Parse startDate as local date to avoid timezone issues
                const [year, month, day] = startDate.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                date.setDate(date.getDate() + i);
                // Format as YYYY-MM-DD using local date
                const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                return renderDayColumn(i, dateString);
              })}
            </ColumnsDirective>
            <Inject services={[Edit]} />
          </GridComponent>
        </div>
      )}
    </div>
  );
});